import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface Plan {
  id: string;
  bezeichnung: string;
  upload_id?: number;
  active: boolean;
  is_active?: boolean;
  activated_at?: string;
  sektion_id: string;
  standort_id: string;
  client_id: string;
  anmerkungen?: string;
  created_at: string;
  updated_at: string;
  // Computed fields
  upload?: FileUpload;
  sektion?: Sektion;
  standort?: Standort;
  client?: Kunde;
}

export interface Sektion {
  id: string;
  name: string;
  beschreibung?: string;
  gebaeude_id: string;
  stockwerk?: number;
  status: boolean;
  created_at: string;
  updated_at: string;
}

export interface Standort {
  id: string;
  name: string;
  nummer: number;
  status?: number;
  kunde_id?: string;
}

export interface Kunde {
  id: string;
  uuid: string;
  nummer?: string;
  name?: string;
  status: boolean;
  suchbegriff?: string;
}

export interface FileUpload {
  id: number;
  user_id?: number;
  uploadDate: string;
  filename?: string;
  original_filename: string;
  file_path?: string;
  file_size?: number;
  mime_type?: string;
  table_name?: string;
  record_id?: string;
  description?: string;
  tags?: any;
  is_public?: boolean;
  download_count?: number;
  last_downloaded?: string;
  checksum?: string;
  user_name?: string;
  mobile_id?: string;
  app_key?: string;
  created_at: string;
  updated_at: string;
  usage_count?: number;
  usage_details?: any;
  is_protected?: boolean;
  user_full_name?: string;
  standort_name?: string;
}

export interface PlanResponse {
  data: Plan[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

@Injectable({
  providedIn: 'root'
})
export class PlanService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Get plans by standort
   */
  getPlansByStandort(standortId: string, page: number = 1, limit: number = 100): Observable<PlanResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<PlanResponse>(`${this.apiUrl}/plan/standort/${standortId}`, { params });
  }

  /**
   * Get a single plan by ID
   */
  getPlan(id: string): Observable<Plan> {
    return this.http.get<Plan>(`${this.apiUrl}/plan/${id}`);
  }

  /**
   * Sign document URL for secure access
   */
  signDokumentUrl(uploadId: number, ttlSeconds: number = 3600, download: boolean = false): Observable<{ result: number; url: string; expiresAt: string }> {
    const params = new HttpParams()
      .set('ttl', ttlSeconds.toString())
      .set('dl', download ? '1' : '0');
    return this.http.get<{ result: number; url: string; expiresAt: string }>(`${this.apiUrl}/fileupload/${uploadId}/sign`, { params });
  }

  /**
   * Save interactive plan (stub for read-only mode - should not be called)
   */
  saveInteractivePlan(
    originalUploadId: number,
    imageDataUrl: string,
    format: 'png' | 'jpeg' = 'jpeg',
    planId?: string
  ): Observable<{result: number, data: FileUpload}> {
    // This method should never be called in read-only mode
    // Return error observable
    return throwError(() => new Error('Save functionality is disabled in read-only mode'));
  }

  /**
   * Download plan PDF (with plan image and room-indikatoren table)
   */
  downloadPlanPDF(planId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/pdf/plan/${planId}`, {
      responseType: 'blob'
    });
  }
}

